import apiCall from './apiCall';
import config from '../config';
import store from '../redux/store';

export const sendChatMessageApi = (message: string, currentPage?: string) => {
  return apiCall({
    method: 'post',
    endpoint: 'api/v2/dashboard-bff/chatbot/chat',
    payload: {
      message,
      context: { currentPage },
    },
  });
};

export const streamChatMessageApi = (
  message: string,
  currentPage: string | undefined,
  onEvent: (event: { type: string;[key: string]: any }) => void,
  aiHelperFieldLabel?: string | null,
  verificationErrorContext?: { errors: string[]; route: string } | null,
): AbortController => {
  const url = `${config().API.API_BASE_URL}/api/v2/dashboard-bff/chatbot/chat/stream`;
  const authToken = store.getState().loginReducer.loginState.token;
  const controller = new AbortController();

  (async () => {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ message, context: { currentPage, aiHelperFieldLabel, verificationErrorContext } }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        onEvent({ type: 'error', content: `HTTP ${resp.status}` });
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let sseBuffer = '';
      let sepIdx: number;

      let done = false;

      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;

        sseBuffer += decoder.decode(result.value, { stream: true });

        while ((sepIdx = sseBuffer.indexOf('\n\n')) !== -1) {
          const frame = sseBuffer.slice(0, sepIdx);
          sseBuffer = sseBuffer.slice(sepIdx + 2);

          const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
          if (!dataLine) continue;

          const jsonStr = dataLine.slice(5).trim();
          if (!jsonStr) continue;

          try {
            onEvent(JSON.parse(jsonStr));
          } catch {
            /* ignore malformed frame */
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      onEvent({ type: 'error', content: String(err?.message ?? err) });
    }
  })();

  return controller;
};

export const clearChatHistoryApi = () => {
  return apiCall({
    method: 'delete',
    endpoint: 'api/v2/dashboard-bff/chatbot/history',
  });
};

export const getSuggestionsApi = (current_step) => {
  return apiCall({
    method: 'get',
    endpoint: `api/v2/dashboard-bff/chatbot/suggestions?current_step=${current_step}`,
  });
};

export const createTicketApi = (payload: {
  title: string;
  description: string;
  comment?: string;
  domain?: string;
  issueType?: string;
  processStep?: string;
  journeyType?: string;
  leadId?: string;
  opportunityId?: string;
  applicationId?: string;
  systemErrorCode?: string;
  affectedService?: string;
  merchantLegalName?: string;
  emailId?: string;
  userId?: string;
  timestamp?: Date;
}) => {
  return apiCall({
    method: 'post',
    endpoint: 'api/v2/dashboard-bff/ticket/create',
    payload: {
      ...payload,
      channel: 'MERCHANT_DASHBOARD',
      source: 'chatbot',
    },
  });
};

export const fetchChatTicketsApi = () => {
  return apiCall({
    method: 'get',
    endpoint: 'api/v2/dashboard-bff/chatbot/tickets',
  });
};

export const initChatbotApi = () => {
  return apiCall({
    method: 'get',
    endpoint: 'api/v2/dashboard-bff/chatbot/init',
  });
};

export const fetchChatHistoryApi = (before?: string, limit = 20) => {
  const params = new URLSearchParams();
  if (before) params.append('before', before);
  params.append('limit', String(limit));
  return apiCall({
    method: 'get',
    endpoint: `api/v2/dashboard-bff/chatbot/history?${params.toString()}`,
  });
};
